import React, { useState, useEffect, useCallback, useMemo } from 'react'
import {
  Box,
  Button,
  Container,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  SelectChangeEvent,
  Tooltip,
  Paper,
  Card,
  CardContent,
  Chip,
  Divider,
  Switch,
  Fade,
  Grow,
  useTheme,
  alpha,
  DialogTitle,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
} from '@mui/material'
import { FormattedMessage, useIntl } from 'react-intl'
import Loading from '../../../../components/Loading'
import axiosInstance from '../../../../utils/axiosInstance'
import { useToast } from '../../../../contexts/ToastContext'
import atsProviders from '../../../../config/atsProviders'
import AtsConfigFields from './AtsConfigFields'
import {
  AtsConnector,
  AtsFormValues,
  atsValuesToConfig,
  initialAtsValues,
  localize,
  validateAtsValues,
} from './atsConnectorSchema'
import AutoPushCvSettings, { AutoPushCvValues, autoPushCvError } from './AutoPushCvSettings'

// Shown when a provider has no logo (or it fails to load)
const FALLBACK_PROVIDER_ICON = '🔗'

const providerLogoUrl = (type: string, connector?: AtsConnector) =>
  connector?.logoUrl ||
  `https://production-talentbridge-public.s3.eu-central-1.amazonaws.com/images/ats-connectors/${type}.png`

type IntegrationConfig = Record<string, unknown>

interface IntegrationData {
  config: IntegrationConfig
  active: boolean
  type: string
  autoLeadPush?: boolean
}

interface Integration {
  id: string
  externalId: string | null
  type: string
  config: IntegrationConfig
  active: boolean
  jobCount?: number
  schedule?: string
  autoLeadPush?: boolean
  cvWaitHours?: number
  onlyPushWithCv?: boolean
  acceptsCvAfterPush?: boolean
  customerAsksForCv?: boolean
  cvFollowUpDays?: number | null
}

type SyncSettings = {
  enabled: boolean
  minutes: number
  autoLeadPush: boolean
} & AutoPushCvValues

// Outcome of a background import, as reported by the server's status endpoint.
interface ImportJobsStatus {
  state: 'running' | 'completed' | 'failed' | 'none'
  result?: {
    totalFetched: number
    totalImported: number
    totalUpdated: number
    totalSkipped: number
    errors: string[]
  }
  error?: string
}

const IMPORT_POLL_INTERVAL_MS = 3000
const IMPORT_POLL_MAX_MS = 30 * 60 * 1000

const wait = (ms: number) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, ms)
  })

export const Integrations = ({ id }: { id: string }) => {
  const intl = useIntl()
  const theme = useTheme()
  const { showToast } = useToast()

  const [selectedProvider, setSelectedProvider] = useState('')
  const [formValues, setFormValues] = useState<AtsFormValues>({})
  const [connectors, setConnectors] = useState<AtsConnector[]>([])
  const [connectorsError, setConnectorsError] = useState(false)
  const [loading, setLoading] = useState(false)
  const [importingId, setImportingId] = useState<string | null>(null)
  const [existingIntegrations, setExistingIntegrations] = useState<Integration[]>([])
  const [loadingIntegrations, setLoadingIntegrations] = useState(false)
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false)
  const [integrationToDelete, setIntegrationToDelete] = useState<string | null>(null)
  const [syncSettings, setSyncSettings] = useState<{ [key: string]: SyncSettings }>({})
  const [showSyncSettings, setShowSyncSettings] = useState<{ [key: string]: boolean }>({})

  // Custom theme extensions
  const customColors = {
    primary: {
      soft: alpha(theme.palette.primary.main, 0.08),
      lighter: alpha(theme.palette.primary.main, 0.16),
    },
    background: {
      neutral: alpha(theme.palette.grey[500], 0.08),
    },
    info: {
      light: alpha(theme.palette.info.main, 0.1),
      soft: alpha(theme.palette.info.main, 0.08),
    },
    error: {
      soft: alpha(theme.palette.error.main, 0.1),
    },
    success: {
      soft: alpha(theme.palette.success.main, 0.08),
    },
    text: {
      disabled: alpha(theme.palette.text.disabled, 0.08),
    },
  }

  const fetchExistingIntegrations = useCallback(async () => {
    setLoadingIntegrations(true)
    try {
      const response = await axiosInstance.get(`/integrations/configurations/customer/${id}`)
      const integrationsWithJobs = await Promise.all(
        response.data.map(async (integration: Integration) => {
          try {
            // Get job count for each integration
            const jobsResponse = await axiosInstance.get(
              `/integrations/configurations/${integration.id}/jobs/count`
            )
            return {
              ...integration,
              jobCount: jobsResponse.data.count,
            }
          } catch (err) {
            console.error(`Error fetching job count for integration ${integration.id}:`, err)
            return {
              ...integration,
              jobCount: 0,
            }
          }
        })
      )
      setExistingIntegrations(integrationsWithJobs)
    } catch (err) {
      console.error('Error fetching integrations:', err)
      showToast(
        intl.formatMessage({
          id: 'integration-load-error',
          defaultMessage: 'Error loading integrations',
        }),
        'error'
      )
    } finally {
      setLoadingIntegrations(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  useEffect(() => {
    axiosInstance
      .get<AtsConnector[]>('/integrations/connectors')
      .then((response) => setConnectors(response.data))
      .catch((err) => {
        console.error('Error fetching ATS connectors:', err)
        setConnectorsError(true)
      })
  }, [])

  // Connectable providers first; the rest stay visible but disabled, with the reason.
  const sortedConnectors = useMemo(
    () => [...connectors].sort((a, b) => Number(b.supported) - Number(a.supported)),
    [connectors]
  )
  const selectedConnector = connectors.find((c) => c.type === selectedProvider)
  const formErrors = useMemo(
    () => (selectedConnector ? validateAtsValues(selectedConnector.config, formValues) : {}),
    [selectedConnector, formValues]
  )
  const isFormValid = !!selectedConnector && Object.keys(formErrors).length === 0

  const providerName = (type: string) =>
    connectors.find((c) => c.type === type)?.name || atsProviders[type]?.name || type

  const missingCapabilityText = (connector: AtsConnector) => {
    const canPush = connector.capabilities.includes('applicants:push')
    const canPull = connector.capabilities.includes('jobs:pull')
    if (!canPush && !canPull) {
      return intl.formatMessage({
        id: 'ats-missing-push-and-pull',
        defaultMessage: "Not available: this ATS can't send jobs or receive applicants",
      })
    }
    if (!canPush) {
      return intl.formatMessage({
        id: 'ats-missing-push',
        defaultMessage: "Not available: this ATS can't receive applicants from Recruspin",
      })
    }
    return intl.formatMessage({
      id: 'ats-missing-pull',
      defaultMessage: "Not available: jobs can't be imported from this ATS",
    })
  }

  const handleProviderChange = (event: SelectChangeEvent) => {
    const newProvider = event.target.value
    if (existingIntegrations.some((integration) => integration.type === newProvider)) {
      showToast(
        intl.formatMessage({
          id: 'integration-type-exists',
          defaultMessage: 'Integration type already exists',
        }),
        'error'
      )
      return
    }
    const connector = connectors.find((c) => c.type === newProvider)
    // Unsupported providers are disabled in the list; this only guards against stale state.
    if (!connector?.supported) return
    setSelectedProvider(newProvider)
    setFormValues(initialAtsValues(connector.config)) // Reset config when provider changes
  }

  const handleSubmit = async () => {
    if (!selectedConnector) return
    if (!isFormValid) {
      showToast(
        intl.formatMessage({
          id: 'fill-required-fields',
          defaultMessage: 'Please fill all required fields',
        }),
        'error'
      )
      return
    }

    setLoading(true)
    try {
      const integrationData: IntegrationData = {
        config: atsValuesToConfig(selectedConnector.config, formValues),
        active: true,
        type: selectedConnector.type,
        autoLeadPush: false, // Default to false for new integrations
      }

      await axiosInstance.post('/integrations/configurations', {
        ...integrationData,
        customerId: id,
      })
      showToast(
        intl.formatMessage({
          id: 'integration-saved',
          defaultMessage: 'Integration saved successfully',
        }),
        'success'
      )

      // Refresh the list of integrations
      await fetchExistingIntegrations()
      setSelectedProvider('')
      setFormValues({})
    } catch (error) {
      console.error('Error saving integration:', error)
      showToast(
        intl.formatMessage({
          id: 'integration-save-error',
          defaultMessage: 'Error saving integration',
        }),
        'error'
      )
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteIntegration = async () => {
    if (!integrationToDelete) return

    setLoading(true)
    try {
      await axiosInstance.delete(`/integrations/configurations/${integrationToDelete}`)
      showToast(
        intl.formatMessage({
          id: 'integration-deleted',
          defaultMessage: 'Integration deleted successfully',
        }),
        'success'
      )
      // Refresh the list of integrations
      await fetchExistingIntegrations()
    } catch (error) {
      console.error('Error deleting integration:', error)
      showToast(
        intl.formatMessage({
          id: 'integration-delete-error',
          defaultMessage: 'Error deleting integration',
        }),
        'error'
      )
    } finally {
      setLoading(false)
      setConfirmDialogOpen(false)
      setIntegrationToDelete(null)
    }
  }

  const openConfirmDialog = (integrationId: string) => {
    setIntegrationToDelete(integrationId)
    setConfirmDialogOpen(true)
  }

  const closeConfirmDialog = () => {
    setConfirmDialogOpen(false)
    setIntegrationToDelete(null)
  }

  // The import runs in the background on the server (a big one outlasts the proxy's request
  // limit), so start it and then poll for the outcome.
  const handleImportJobs = async (integrationId: string) => {
    setImportingId(integrationId)
    try {
      await axiosInstance.post(`/integrations/configurations/${integrationId}/import-jobs`)

      const startedAt = Date.now()
      let status: ImportJobsStatus = { state: 'running' }
      while (status.state === 'running' && Date.now() - startedAt < IMPORT_POLL_MAX_MS) {
        // eslint-disable-next-line no-await-in-loop
        await wait(IMPORT_POLL_INTERVAL_MS)
        // eslint-disable-next-line no-await-in-loop
        const response = await axiosInstance.get<ImportJobsStatus>(
          `/integrations/configurations/${integrationId}/import-jobs/status`
        )
        status = response.data
      }

      const result = status.result
      if (status.state === 'completed' && result && result.errors.length === 0) {
        showToast(
          intl.formatMessage(
            {
              id: 'jobs-import-finished',
              defaultMessage: 'Jobs imported: {created} new, {updated} updated',
            },
            { created: result.totalImported, updated: result.totalUpdated }
          ),
          'success'
        )
      } else if (status.state === 'completed' && result && result.totalFetched > 0) {
        showToast(
          intl.formatMessage(
            {
              id: 'jobs-import-partial',
              defaultMessage: 'Imported {count} jobs, {failedCount} could not be saved',
            },
            { count: result.totalImported + result.totalUpdated, failedCount: result.totalSkipped }
          ),
          'warning'
        )
      } else if (status.state === 'running') {
        showToast(
          intl.formatMessage({
            id: 'jobs-import-still-running',
            defaultMessage: 'The import is still running. Check back later.',
          }),
          'warning'
        )
      } else {
        showToast(
          intl.formatMessage({ id: 'jobs-import-error', defaultMessage: 'Error importing jobs' }),
          'error'
        )
      }
      // Refresh the list of integrations to update job counts
      await fetchExistingIntegrations()
    } catch (error) {
      console.error('Error importing jobs:', error)
      showToast(
        intl.formatMessage({ id: 'jobs-import-error', defaultMessage: 'Error importing jobs' }),
        'error'
      )
    } finally {
      setImportingId(null)
    }
  }

  // const handleSyncIntervalChange = async (integrationId: string, schedule?: string) => {
  //   setLoading(true)
  //   try {
  //     await axiosInstance.put(`/integrations/configurations/${integrationId}`, {
  //       schedule,
  //     })
  //     showToast(
  //       intl.formatMessage({
  //         id: 'sync-interval-updated',
  //         defaultMessage: 'Sync interval updated successfully',
  //       }),
  //       'success'
  //     )
  //     // Refresh the list of integrations
  //     await fetchExistingIntegrations()
  //   } catch (error) {
  //     console.error('Error updating sync interval:', error)
  //     showToast(
  //       intl.formatMessage({
  //         id: 'sync-interval-update-error',
  //         defaultMessage: 'Error updating sync interval',
  //       }),
  //       'error'
  //     )
  //   } finally {
  //     setLoading(false)
  //   }
  // }

  const getSyncIntervalMinutes = (schedule?: string) => {
    if (!schedule || schedule === '') return 0

    // Extract minutes from the cron expression: "*/30 * * * *"
    const match = schedule.match(/\*\/(\d+)\s+\*\s+\*\s+\*\s+\*/)
    return match && match[1] ? parseInt(match[1], 10) : 30 // Default to 30 if pattern doesn't match
  }

  // New functions for managing sync settings before saving
  const toggleSyncSettingsView = (integrationId: string) => {
    setShowSyncSettings((prev) => ({
      ...prev,
      [integrationId]: !prev[integrationId],
    }))
  }

  const updateSyncSettingsLocally = (
    integrationId: string,
    field: 'enabled' | 'minutes' | 'autoLeadPush',
    value: boolean | number
  ) => {
    setSyncSettings((prev) => ({
      ...prev,
      [integrationId]: {
        ...prev[integrationId],
        [field]: value,
      },
    }))
  }

  const updateAutoPushCvLocally = (integrationId: string, values: AutoPushCvValues) => {
    setSyncSettings((prev) => ({
      ...prev,
      [integrationId]: { ...prev[integrationId], ...values },
    }))
  }

  const saveSyncSettings = async (integrationId: string) => {
    const settings = syncSettings[integrationId]
    if (!settings) return

    const cvSettingsError = settings.autoLeadPush ? autoPushCvError(settings) : ''
    if (cvSettingsError) {
      showToast(cvSettingsError, 'error')
      return
    }

    // Generate correct cron pattern: */minutes * * * * (every X minutes)
    const schedule = settings.enabled ? `*/${settings.minutes} * * * *` : ''

    setLoading(true)
    try {
      await axiosInstance.put(`/integrations/configurations/${integrationId}`, {
        schedule,
        autoLeadPush: settings.autoLeadPush,
        // Only sent with auto push on: with it off they are hidden and have no effect, and
        // a hidden invalid combination must not block saving the other settings.
        ...(settings.autoLeadPush
          ? { cvWaitHours: settings.cvWaitHours, onlyPushWithCv: settings.onlyPushWithCv }
          : {}),
      })
      showToast(
        intl.formatMessage({
          id: 'sync-settings-updated',
          defaultMessage: 'Sync settings updated successfully',
        }),
        'success'
      )
      // Refresh the list of integrations
      await fetchExistingIntegrations()
      // Hide settings panel after save
      setShowSyncSettings((prev) => ({
        ...prev,
        [integrationId]: false,
      }))
    } catch (error) {
      console.error('Error updating sync settings:', error)
      showToast(
        intl.formatMessage({
          id: 'sync-settings-update-error',
          defaultMessage: 'Error updating sync settings',
        }),
        'error'
      )
    } finally {
      setLoading(false)
    }
  }

  // Load existing integrations when component mounts
  useEffect(() => {
    if (id) {
      fetchExistingIntegrations()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  // Initialize sync settings when integrations are loaded
  useEffect(() => {
    const initialSyncSettings: { [key: string]: SyncSettings } = {}
    existingIntegrations.forEach((integration) => {
      initialSyncSettings[integration.id] = {
        enabled: !!integration.schedule && integration.schedule !== '',
        minutes: getSyncIntervalMinutes(integration.schedule) || 30,
        autoLeadPush: integration.autoLeadPush || false,
        cvWaitHours: integration.cvWaitHours ?? 0,
        onlyPushWithCv: integration.onlyPushWithCv ?? false,
      }
    })
    setSyncSettings(initialSyncSettings)
  }, [existingIntegrations])

  if (loading || loadingIntegrations) return <Loading />

  return (
    <Container maxWidth="lg" className="integrationsContainer subFormContainer">
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 2,
          bgcolor: 'background.paper',
          mb: 4,
        }}
      >
        <Typography
          variant="h4"
          component="h1"
          sx={{
            mb: 3,
            fontWeight: 600,
            color: 'primary.main',
            fontSize: { xs: '1.5rem', md: '1.75rem' },
          }}
        >
          <FormattedMessage id="integrations" defaultMessage="Integrations" />
        </Typography>

        <Grid container spacing={4}>
          {existingIntegrations.length > 0 && (
            <Grid item xs={12}>
              <Typography
                variant="h6"
                sx={{
                  mb: 2.5,
                  fontSize: '1.1rem',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <span
                  role="img"
                  aria-label="connected"
                  style={{ marginRight: '8px', fontSize: '1.2rem' }}
                >
                  🔌
                </span>
                <FormattedMessage
                  id="existing-integrations"
                  defaultMessage="Connected Integrations"
                />
                <Chip
                  label={existingIntegrations.length}
                  size="small"
                  sx={{ ml: 1, height: '20px', fontSize: '0.75rem' }}
                />
              </Typography>

              <Grid container spacing={2}>
                {existingIntegrations.map((integration) => {
                  const integrationName = providerName(integration.type)

                  return (
                    <Grid item xs={12} key={integration.id}>
                      <Card
                        sx={{
                          borderRadius: 2,
                          transition: 'all 0.25s ease-in-out',
                          border: '1px solid',
                          borderColor: 'divider',
                          boxShadow: 'none',
                          '&:hover': {
                            borderColor: 'primary.light',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                            transform: 'translateY(-2px)',
                          },
                        }}
                      >
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                          <Grid container spacing={2}>
                            {/* Left column - Integration info */}
                            <Grid
                              item
                              xs={12}
                              md={4}
                              sx={{ display: 'flex', flexDirection: 'column' }}
                            >
                              {/* Integration header */}
                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <Box
                                  sx={{
                                    width: 40,
                                    height: 40,
                                    borderRadius: '8px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    bgcolor: customColors.primary.soft,
                                    mr: 1.5,
                                    fontSize: '1.25rem',
                                    overflow: 'hidden',
                                  }}
                                >
                                  <img
                                    src={providerLogoUrl(
                                      integration.type,
                                      connectors.find((c) => c.type === integration.type)
                                    )}
                                    alt={`${integrationName} logo`}
                                    style={{
                                      width: '100%',
                                      height: '100%',
                                      objectFit: 'contain',
                                    }}
                                    onError={(e) => {
                                      // Fallback to icon if image fails to load
                                      e.currentTarget.style.display = 'none'
                                      e.currentTarget.parentElement!.textContent =
                                        FALLBACK_PROVIDER_ICON
                                    }}
                                  />
                                </Box>
                                <Box>
                                  <Typography
                                    variant="subtitle1"
                                    sx={{ fontWeight: 600, lineHeight: 1.2, mb: 0.5 }}
                                  >
                                    {integrationName}
                                  </Typography>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Chip
                                      label="ATS"
                                      size="small"
                                      sx={{
                                        borderRadius: '6px',
                                        fontWeight: 500,
                                        fontSize: '0.65rem',
                                        height: '20px',
                                      }}
                                    />
                                    {integration.active && (
                                      <Box
                                        sx={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          color: theme.palette.success.main,
                                          fontSize: '0.7rem',
                                        }}
                                      >
                                        <Box
                                          component="span"
                                          sx={{
                                            width: 6,
                                            height: 6,
                                            borderRadius: '50%',
                                            bgcolor: theme.palette.success.main,
                                            mr: 0.5,
                                          }}
                                        />
                                        Active
                                      </Box>
                                    )}
                                  </Box>
                                </Box>
                              </Box>

                              {/* Stats */}
                              <Grid container spacing={1.5} sx={{ mb: 2 }}>
                                <Grid item xs={12}>
                                  <Box
                                    sx={{
                                      p: 1.5,
                                      borderRadius: 1.5,
                                      bgcolor: customColors.background.neutral,
                                      height: '100%',
                                    }}
                                  >
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{ display: 'block' }}
                                    >
                                      External ID
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontWeight: 500,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                      }}
                                    >
                                      {integration.externalId || '—'}
                                    </Typography>
                                  </Box>
                                </Grid>
                                <Grid item xs={12}>
                                  <Box
                                    sx={{
                                      p: 1.5,
                                      borderRadius: 1.5,
                                      bgcolor: integration.jobCount
                                        ? customColors.success.soft
                                        : customColors.text.disabled,
                                      height: '100%',
                                      color: integration.jobCount
                                        ? 'success.dark'
                                        : 'text.secondary',
                                    }}
                                  >
                                    <Typography variant="caption" sx={{ display: 'block' }}>
                                      Jobs Count
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                      {integration.jobCount || 0}
                                    </Typography>
                                  </Box>
                                </Grid>
                              </Grid>

                              {/* Action Buttons (mobile only) */}
                              <Box
                                sx={{
                                  display: { xs: 'flex', md: 'none' },
                                  gap: 1,
                                  mt: 'auto',
                                  justifyContent: 'space-between',
                                  mb: 2,
                                }}
                              >
                                <Button
                                  variant="outlined"
                                  size="small"
                                  onClick={() => toggleSyncSettingsView(integration.id)}
                                  sx={{
                                    flex: 1,
                                    borderRadius: 1.5,
                                    textTransform: 'none',
                                    fontWeight: 500,
                                    borderColor: 'divider',
                                    color: 'text.primary',
                                    '&:hover': {
                                      borderColor: 'primary.main',
                                      bgcolor: customColors.primary.soft,
                                    },
                                  }}
                                >
                                  {showSyncSettings[integration.id] ? 'Hide settings' : 'Configure'}
                                </Button>

                                <Button
                                  variant="contained"
                                  size="small"
                                  color="primary"
                                  onClick={() => handleImportJobs(integration.id)}
                                  disabled={importingId === integration.id}
                                  startIcon={
                                    <span role="img" aria-label="import">
                                      📥
                                    </span>
                                  }
                                  sx={{
                                    flex: 1,
                                    textTransform: 'none',
                                    borderRadius: 1.5,
                                    fontWeight: 500,
                                  }}
                                >
                                  {importingId === integration.id ? 'Importing...' : 'Import Jobs'}
                                </Button>
                              </Box>
                            </Grid>

                            {/* Center column - Status */}
                            <Grid
                              item
                              xs={12}
                              md={4}
                              sx={{ display: 'flex', flexDirection: 'column' }}
                            >
                              <Box
                                sx={{
                                  p: 2,
                                  bgcolor: customColors.background.neutral,
                                  borderRadius: 2,
                                  height: '100%',
                                  display: 'flex',
                                  flexDirection: 'column',
                                }}
                              >
                                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                                  Sync Status
                                </Typography>

                                {/* Sync status indicators */}
                                <Box sx={{ mb: 2.5 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                                    <Box
                                      component="span"
                                      sx={{
                                        width: 8,
                                        height: 8,
                                        borderRadius: '50%',
                                        bgcolor: integration.schedule
                                          ? theme.palette.success.main
                                          : theme.palette.text.disabled,
                                        mr: 1,
                                      }}
                                    />
                                    <Typography variant="body2" color="text.primary">
                                      {integration.schedule ? 'Sync is active' : 'Sync is inactive'}
                                    </Typography>
                                  </Box>

                                  {!!integration.schedule && (
                                    <Box sx={{ ml: 2.5, mb: 1.5 }}>
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ display: 'block' }}
                                      >
                                        Runs every {getSyncIntervalMinutes(integration.schedule)}{' '}
                                        minutes
                                      </Typography>
                                    </Box>
                                  )}

                                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                                    <Box
                                      component="span"
                                      sx={{
                                        width: 8,
                                        height: 8,
                                        borderRadius: '50%',
                                        bgcolor:
                                          integration.autoLeadPush === true
                                            ? theme.palette.info.main
                                            : theme.palette.text.disabled,
                                        mr: 1,
                                      }}
                                    />
                                    <Typography variant="body2" color="text.primary">
                                      {integration.autoLeadPush === true
                                        ? 'Auto lead push is enabled'
                                        : 'Auto lead push is disabled'}
                                    </Typography>
                                  </Box>
                                  {integration.autoLeadPush === true &&
                                    !!integration.cvWaitHours && (
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ display: 'block', mt: -1, mb: 1.5, ml: 2 }}
                                      >
                                        Waits up to {integration.cvWaitHours} h for the CV
                                        {integration.onlyPushWithCv
                                          ? ', only leads with a CV are sent'
                                          : ''}
                                      </Typography>
                                    )}
                                </Box>

                                {/* Last update - placeholder */}
                                <Box sx={{ mt: 'auto' }}>
                                  <Typography variant="caption" color="text.secondary">
                                    Last updated: {new Date().toLocaleDateString()}
                                  </Typography>
                                </Box>
                              </Box>
                            </Grid>

                            {/* Right column - Actions */}
                            <Grid
                              item
                              xs={12}
                              md={4}
                              sx={{ display: 'flex', flexDirection: 'column' }}
                            >
                              {/* Action Buttons (desktop only) */}
                              <Box
                                sx={{
                                  display: { xs: 'none', md: 'flex' },
                                  gap: 1.5,
                                  mb: 2,
                                  flexDirection: 'column',
                                }}
                              >
                                <Button
                                  variant="contained"
                                  size="medium"
                                  color="primary"
                                  onClick={() => handleImportJobs(integration.id)}
                                  disabled={importingId === integration.id}
                                  startIcon={
                                    <span role="img" aria-label="import">
                                      📥
                                    </span>
                                  }
                                  sx={{
                                    textTransform: 'none',
                                    borderRadius: 1.5,
                                    fontWeight: 500,
                                    py: 1,
                                  }}
                                >
                                  {importingId === integration.id ? 'Importing...' : 'Import Jobs'}
                                </Button>

                                <Button
                                  variant="outlined"
                                  size="medium"
                                  onClick={() => toggleSyncSettingsView(integration.id)}
                                  sx={{
                                    borderRadius: 1.5,
                                    textTransform: 'none',
                                    fontWeight: 500,
                                    borderColor: 'divider',
                                    color: 'text.primary',
                                    py: 1,
                                    '&:hover': {
                                      borderColor: 'primary.main',
                                      bgcolor: customColors.primary.soft,
                                    },
                                  }}
                                >
                                  {showSyncSettings[integration.id]
                                    ? 'Hide sync settings'
                                    : 'Configure sync settings'}
                                </Button>

                                <Button
                                  variant="text"
                                  color="error"
                                  size="small"
                                  onClick={() => openConfirmDialog(integration.id)}
                                  sx={{
                                    fontSize: '0.8rem',
                                    opacity: 0.7,
                                    '&:hover': {
                                      opacity: 1,
                                      bgcolor: customColors.error.soft,
                                    },
                                  }}
                                >
                                  <FormattedMessage
                                    id="delete"
                                    defaultMessage="Delete Integration"
                                  />
                                </Button>
                              </Box>

                              {/* Mobile delete button */}
                              <Box
                                sx={{
                                  display: { xs: 'flex', md: 'none' },
                                  justifyContent: 'center',
                                  mb: 2,
                                }}
                              >
                                <Button
                                  variant="text"
                                  color="error"
                                  size="small"
                                  onClick={() => openConfirmDialog(integration.id)}
                                  sx={{
                                    fontSize: '0.8rem',
                                    opacity: 0.7,
                                    '&:hover': {
                                      opacity: 1,
                                      bgcolor: customColors.error.soft,
                                    },
                                  }}
                                >
                                  <FormattedMessage
                                    id="delete"
                                    defaultMessage="Delete Integration"
                                  />
                                </Button>
                              </Box>
                            </Grid>
                          </Grid>

                          {/* Sync settings panel - full width */}
                          <Grow
                            in={showSyncSettings[integration.id]}
                            mountOnEnter
                            unmountOnExit
                            style={{ transformOrigin: '0 0 0' }}
                          >
                            <Box>
                              <Divider sx={{ mt: 3, mb: 3 }} />

                              <Grid container spacing={2}>
                                <Grid item xs={12} sm={8} md={6} lg={5}>
                                  <Typography
                                    variant="subtitle2"
                                    sx={{
                                      mb: 2,
                                      fontWeight: 600,
                                      display: 'flex',
                                      alignItems: 'center',
                                    }}
                                  >
                                    <span
                                      role="img"
                                      aria-label="settings"
                                      style={{ marginRight: '8px', fontSize: '0.9rem' }}
                                    >
                                      ⚙️
                                    </span>
                                    Sync Configuration
                                  </Typography>

                                  <Paper
                                    elevation={0}
                                    sx={{
                                      p: 2.5,
                                      borderRadius: 2,
                                      border: '1px solid',
                                      borderColor: 'divider',
                                    }}
                                  >
                                    <Grid container spacing={2}>
                                      {/* Enable periodic sync toggle */}
                                      <Grid item xs={12}>
                                        <Box
                                          sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            py: 0.5,
                                          }}
                                        >
                                          <Typography
                                            variant="body2"
                                            color="text.primary"
                                            sx={{ fontWeight: 500 }}
                                          >
                                            Enable periodic sync
                                          </Typography>
                                          <Switch
                                            size="small"
                                            checked={syncSettings[integration.id]?.enabled || false}
                                            onChange={(e) =>
                                              updateSyncSettingsLocally(
                                                integration.id,
                                                'enabled',
                                                e.target.checked
                                              )
                                            }
                                            color="primary"
                                          />
                                        </Box>
                                      </Grid>

                                      {/* Sync interval */}
                                      {syncSettings[integration.id]?.enabled && (
                                        <Grid item xs={12}>
                                          <Box
                                            sx={{
                                              display: 'flex',
                                              alignItems: 'center',
                                              justifyContent: 'space-between',
                                              px: 1,
                                              py: 1.5,
                                              bgcolor: customColors.background.neutral,
                                              borderRadius: 1.5,
                                            }}
                                          >
                                            <Typography variant="body2" color="text.secondary">
                                              Sync interval:
                                            </Typography>
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                              <TextField
                                                size="small"
                                                type="number"
                                                variant="outlined"
                                                value={syncSettings[integration.id]?.minutes || 30}
                                                onChange={(e) => {
                                                  const minutes = parseInt(e.target.value, 10)
                                                  if (minutes > 0) {
                                                    updateSyncSettingsLocally(
                                                      integration.id,
                                                      'minutes',
                                                      minutes
                                                    )
                                                  }
                                                }}
                                                inputProps={{
                                                  min: 1,
                                                  style: {
                                                    width: '45px',
                                                    textAlign: 'center',
                                                  },
                                                }}
                                                sx={{
                                                  '& .MuiOutlinedInput-root': {
                                                    borderRadius: 1,
                                                    bgcolor: 'background.paper',
                                                  },
                                                }}
                                              />
                                              <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                sx={{ ml: 1 }}
                                              >
                                                minutes
                                              </Typography>
                                            </Box>
                                          </Box>
                                        </Grid>
                                      )}

                                      {/* Auto push toggle */}
                                      <Grid item xs={12}>
                                        <Box
                                          sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            py: 0.5,
                                          }}
                                        >
                                          <Tooltip
                                            title="When enabled, new leads will be automatically pushed to this ATS integration"
                                            placement="top"
                                            arrow
                                          >
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                              <Typography
                                                variant="body2"
                                                color="text.primary"
                                                sx={{ fontWeight: 500 }}
                                              >
                                                Auto push leads
                                              </Typography>
                                              <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                sx={{ ml: 0.5 }}
                                              >
                                                ⓘ
                                              </Typography>
                                            </Box>
                                          </Tooltip>
                                          <Switch
                                            size="small"
                                            checked={
                                              syncSettings[integration.id]?.autoLeadPush || false
                                            }
                                            onChange={(e) =>
                                              updateSyncSettingsLocally(
                                                integration.id,
                                                'autoLeadPush',
                                                e.target.checked
                                              )
                                            }
                                            color="primary"
                                          />
                                        </Box>
                                      </Grid>

                                      {/* CV handling for auto push */}
                                      {syncSettings[integration.id]?.autoLeadPush && (
                                        <Grid item xs={12}>
                                          <AutoPushCvSettings
                                            values={{
                                              cvWaitHours:
                                                syncSettings[integration.id]?.cvWaitHours ?? 0,
                                              onlyPushWithCv:
                                                syncSettings[integration.id]?.onlyPushWithCv ??
                                                false,
                                            }}
                                            onChange={(values) =>
                                              updateAutoPushCvLocally(integration.id, values)
                                            }
                                            acceptsCvAfterPush={integration.acceptsCvAfterPush}
                                            customerAsksForCv={integration.customerAsksForCv}
                                            cvFollowUpDays={integration.cvFollowUpDays}
                                          />
                                        </Grid>
                                      )}

                                      {/* Save button */}
                                      <Grid item xs={12} sx={{ mt: 1 }}>
                                        <Button
                                          fullWidth
                                          variant="contained"
                                          size="medium"
                                          color="primary"
                                          onClick={() => saveSyncSettings(integration.id)}
                                          sx={{
                                            textTransform: 'none',
                                            borderRadius: 1.5,
                                            py: 0.75,
                                          }}
                                        >
                                          Save Settings
                                        </Button>
                                      </Grid>
                                    </Grid>
                                  </Paper>
                                </Grid>
                              </Grid>
                            </Box>
                          </Grow>
                        </CardContent>
                      </Card>
                    </Grid>
                  )
                })}
              </Grid>
            </Grid>
          )}

          <Grid item xs={12}>
            <Box
              sx={{
                position: 'relative',
                mb: 2.5,
                mt: existingIntegrations.length > 0 ? 4 : 0,
              }}
            >
              <Divider sx={{ mb: 4, mt: existingIntegrations.length > 0 ? 2 : 0 }} />

              <Typography
                variant="h6"
                sx={{
                  mb: 3,
                  fontSize: '1.1rem',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <span
                  role="img"
                  aria-label="add"
                  style={{ marginRight: '8px', fontSize: '1.2rem' }}
                >
                  ➕
                </span>
                <FormattedMessage id="add-integration" defaultMessage="Add New Integration" />
              </Typography>

              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: 2,
                  bgcolor: customColors.background.neutral,
                  border: '1px dashed',
                  borderColor: 'divider',
                }}
              >
                <Box sx={{ maxWidth: 600, mx: 'auto' }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 3, textAlign: 'center' }}
                  >
                    Connect your ATS provider to automatically import jobs and synchronize data
                  </Typography>

                  <FormControl
                    fullWidth
                    sx={{
                      mb: 3,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 1.5,
                        bgcolor: 'background.paper',
                      },
                    }}
                  >
                    <InputLabel id="integration-provider-label">
                      <FormattedMessage
                        id="select-integration-provider"
                        defaultMessage="Select Integration Provider"
                      />
                    </InputLabel>
                    <Select
                      labelId="integration-provider-label"
                      id="integration-provider"
                      value={selectedProvider}
                      label={intl.formatMessage({
                        id: 'select-integration-provider',
                        defaultMessage: 'Select Integration Provider',
                      })}
                      onChange={handleProviderChange}
                    >
                      {sortedConnectors.map((connector) => {
                        const isProviderUsed = existingIntegrations.some(
                          (integration) => integration.type === connector.type
                        )
                        return (
                          <MenuItem
                            key={connector.type}
                            value={connector.type}
                            disabled={isProviderUsed || !connector.supported}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              py: 1.5,
                            }}
                          >
                            <Box
                              sx={{
                                width: 32,
                                height: 32,
                                borderRadius: '6px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bgcolor: alpha(theme.palette.primary.main, 0.08),
                                mr: 1.5,
                                fontSize: '1.1rem',
                                overflow: 'hidden',
                                flexShrink: 0,
                              }}
                            >
                              <img
                                src={providerLogoUrl(connector.type, connector)}
                                alt={`${connector.name} logo`}
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'contain',
                                }}
                                onError={(e) => {
                                  // Fallback to icon if image fails to load
                                  e.currentTarget.style.display = 'none'
                                  e.currentTarget.parentElement!.textContent =
                                    FALLBACK_PROVIDER_ICON
                                }}
                              />
                            </Box>
                            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                              {connector.name}
                              {!connector.supported && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ display: 'block', whiteSpace: 'normal' }}
                                >
                                  {missingCapabilityText(connector)}
                                </Typography>
                              )}
                            </Box>
                            {isProviderUsed && (
                              <Chip
                                label={intl.formatMessage({
                                  id: 'already-configured',
                                  defaultMessage: 'Already Connected',
                                })}
                                size="small"
                                sx={{
                                  ml: 1,
                                  fontSize: '0.65rem',
                                  height: '20px',
                                  bgcolor: 'action.hover',
                                }}
                              />
                            )}
                          </MenuItem>
                        )
                      })}
                    </Select>
                    <FormHelperText error={connectorsError}>
                      {connectorsError
                        ? intl.formatMessage({
                            id: 'ats-connectors-load-error',
                            defaultMessage:
                              'Could not load the list of ATS providers. Reload the page to try again.',
                          })
                        : intl.formatMessage({
                            id: 'ats-unsupported-hint',
                            defaultMessage:
                              'Greyed-out providers cannot be connected: an integration must both import jobs and receive applicants.',
                          })}
                    </FormHelperText>
                  </FormControl>

                  {selectedProvider && (
                    <Fade in={!!selectedProvider}>
                      <Box sx={{ mb: 3 }}>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            mb: 2,
                            fontWeight: 600,
                          }}
                        >
                          Connection Details
                        </Typography>

                        {selectedConnector?.description && (
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            {localize(selectedConnector.description, intl.locale, '')}
                          </Typography>
                        )}

                        <Box
                          sx={{
                            p: 2.5,
                            bgcolor: 'background.paper',
                            borderRadius: 2,
                            border: '1px solid',
                            borderColor: 'divider',
                          }}
                        >
                          {selectedConnector && (
                            <AtsConfigFields
                              schema={selectedConnector.config}
                              values={formValues}
                              errors={formErrors}
                              onChange={setFormValues}
                            />
                          )}
                        </Box>
                      </Box>
                    </Fade>
                  )}

                  <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <Button
                      variant="contained"
                      color="primary"
                      size="large"
                      disableElevation
                      onClick={handleSubmit}
                      disabled={loading || !isFormValid}
                      sx={{
                        px: 4,
                        py: 1,
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 600,
                      }}
                    >
                      <FormattedMessage
                        id="save-integration"
                        defaultMessage="Connect Integration"
                      />
                    </Button>
                  </Box>
                </Box>
              </Paper>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Confirm Delete Dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={closeConfirmDialog}
        aria-labelledby="confirm-dialog-title"
        aria-describedby="confirm-dialog-description"
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxWidth: 500,
          },
        }}
      >
        <DialogTitle id="confirm-dialog-title" sx={{ pb: 1, pt: 2.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', color: 'error.main' }}>
            <span
              role="img"
              aria-label="warning"
              style={{ marginRight: '8px', fontSize: '1.2rem' }}
            >
              ⚠️
            </span>
            {intl.formatMessage({
              id: 'delete-integration-confirm-title',
              defaultMessage: 'Delete Integration',
            })}
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="confirm-dialog-description" sx={{ color: 'text.primary', mb: 1 }}>
            {intl.formatMessage({
              id: 'delete-integration-confirm',
              defaultMessage: 'Are you sure you want to delete this integration?',
            })}
          </DialogContentText>
          <Box
            sx={{
              p: 2,
              borderRadius: 1.5,
              bgcolor: customColors.info.light,
              color: 'info.dark',
              fontSize: '0.875rem',
              mt: 2,
            }}
          >
            <span role="img" aria-label="info" style={{ marginRight: '6px' }}>
              ℹ️
            </span>
            {intl.formatMessage({
              id: 'delete-integration-jobs-preserved',
              defaultMessage:
                'The integration will be deleted from both our system and the external ATS service, but any related jobs that have been imported will be preserved.',
            })}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={closeConfirmDialog}
            variant="outlined"
            sx={{
              borderRadius: 1.5,
              textTransform: 'none',
              fontWeight: 500,
            }}
          >
            {intl.formatMessage({ id: 'cancel', defaultMessage: 'Cancel' })}
          </Button>
          <Button
            onClick={handleDeleteIntegration}
            color="error"
            variant="contained"
            disableElevation
            sx={{
              borderRadius: 1.5,
              textTransform: 'none',
              fontWeight: 500,
              ml: 1,
            }}
          >
            {intl.formatMessage({ id: 'delete', defaultMessage: 'Delete' })}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  )
}

export default Integrations
